// Браузер веток дебютов (#/train/openings). Как opening explorer в расширении:
// сверху чипы топ-3 самых частых дебютов юзера, доска проигрывает первые ходы
// главной линии выбранного дебюта, под доской — продолжения из Lichess explorer
// (SAN + доля партий + W/D/L-полоска). Тап по строке углубляет ветку. Тулбар
// разбора (createGameNav): ⏮ в начало линии · ◀ назад · ▶ redo · ⏭ redo до конца ·
// 💡 стрелка топ-1 хода теории · 🤖 тумблер игры с движком · ⇅ переворот доски.
// 🤖 отдаёт текущую позицию в PlayEngineController (сторона = низ доски); выход
// возвращает в браузер веток.
import { Chess } from 'chess.js';
import { MiniBoard } from '@ext/panel/board/mini-board';
import { explorerRows, type ExplorerRow } from '@ext/core/openings-study';
import { t } from '../i18n';
import { state } from '../state';
import { hideMainButton, showBackButton, haptic } from '../tg';
import { trackTraining } from '../services/track';
import { fetchExplorer } from '../services/explorer';
import { normalizeExplorer, topOpenings, type TopOpening } from '../services/openings-train';
import { BranchNav, topTheoryUci } from '../services/branch-nav';
import { PriorityScheduler } from '../services/analysis';
import { analyze as loaderAnalyze } from '../services/engine-loader';
import { PLAY_OPTS } from '../services/play-engine';
import { PlayEngineController } from './play-engine';
import { BoardToolbar, type BoardHost } from './board-toolbar';

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c);
}

/** Применить UCI-ход к позиции: {fen-после, from, to} или null, если нелегален. */
function applyUci(fen: string, uci: string): { fen: string; from: string; to: string } | null {
  try {
    const c = new Chess(fen);
    const m = c.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci[4] || undefined });
    return m ? { fen: c.fen(), from: m.from, to: m.to } : null;
  } catch {
    return null;
  }
}

function shell(): string {
  return `
    <header class="topbar">
      <span class="back" data-back>${t('top.back')}</span>
      <span class="top-title"><span class="top-logo" aria-hidden="true"></span>${t('train.openings_title')}</span>
      <span class="top-action"></span>
    </header>
    <section class="review-head train-head">
      <div class="eyebrow">${t('topen.eyebrow')}</div>
      <h1 style="font-size:24px;margin-top:4px">${t('topen.header')}</h1>
    </section>
    <p class="muted tiny topen-intro">${t('topen.intro')}</p>
    <div class="topen-chips" data-chips></div>
    <section class="train-board-card">
      <div class="board-stage" data-board></div>
    </section>
    <div data-browse>
      <div class="row between topen-bar"><span class="ex-name" data-open-name></span></div>
      <div data-nav></div>
      <section class="ex-moves" data-moves></section>
    </div>
    <div data-play-host></div>`;
}

export function renderTrainOpenings(root: HTMLElement): void {
  if (!state.nick) {
    location.hash = '#/';
    return;
  }
  if (!state.games.length) {
    location.hash = '#/games';
    return;
  }
  trackTraining('train_openings');

  root.className = 'app v2';
  root.innerHTML = shell();
  hideMainButton();
  const back = (): void => {
    location.hash = '#/train';
  };
  showBackButton(back);
  root.querySelector<HTMLElement>('[data-back]')?.addEventListener('click', back);

  const chipsEl = root.querySelector<HTMLElement>('[data-chips]')!;
  const boardEl = root.querySelector<HTMLElement>('[data-board]')!;
  const nameEl = root.querySelector<HTMLElement>('[data-open-name]')!;
  const movesEl = root.querySelector<HTMLElement>('[data-moves]')!;

  const sched = new PriorityScheduler(loaderAnalyze);
  let play: PlayEngineController | null = null;

  // --- состояние браузера ------------------------------------------------------
  let current: TopOpening | null = null;
  let orient: 'white' | 'black' = 'white';
  const nav = new BranchNav(START_FEN, null);
  let showBest = false; // 💡 стрелка топ-1 хода теории — выключена по умолчанию
  let topUci: string | null = null; // самый популярный ход теории в текущей позиции
  let seq = 0; // инвалидация устаревших ответов explorer

  const board = new MiniBoard(boardEl, {
    onMove: (san, fen) => {
      if (play?.active) play.handleUserMove(san, fen);
      else browseMove(fen);
    },
  });

  play = new PlayEngineController({
    board,
    host: root.querySelector<HTMLElement>('[data-play-host]')!,
    analyze: (fen) => sched.requestPriority(fen, PLAY_OPTS),
    exitLabel: t('play.exit_train'),
    onExit: () => {
      setPlayingUi(false);
      drawBoard();
      void drawMoves();
      toolbar.onPlayExit();
    },
    onHintBusy: (busy) => toolbar.setHintBusy(busy),
  });

  function updateArrow(): void {
    if (!showBest || !topUci) {
      board.setArrow(null);
      return;
    }
    board.setArrow({ from: topUci.slice(0, 2), to: topUci.slice(2, 4) });
  }

  function drawBoard(): void {
    board.setArrow(null);
    board.setOrientation(orient);
    board.setPosition(nav.fen, nav.move);
    toolbar.refresh();
  }

  async function drawMoves(): Promise<void> {
    const my = ++seq;
    const fen = nav.fen;
    topUci = null;
    movesEl.innerHTML = `<p class="muted tiny">${t('topen.loading')}</p>`;
    const raw = await fetchExplorer(fen);
    if (my !== seq) return; // ушли глубже/переключились, пока грузили
    const rows = raw ? explorerRows(normalizeExplorer(fen, raw), 5) : [];
    topUci = topTheoryUci(rows);
    updateArrow();
    if (!rows.length) {
      movesEl.innerHTML = `<p class="muted tiny">${t('topen.book_end')}</p>`;
      return;
    }
    movesEl.replaceChildren(...rows.map((r) => rowEl(fen, r)));
  }

  function rowEl(fen: string, r: ExplorerRow): HTMLElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ex-row';
    btn.innerHTML = `
      <span class="ex-san"></span>
      <span class="ex-pop"></span>
      <span class="ex-wdl">
        <i class="ex-w" style="width:${r.whitePct}%"></i><i class="ex-d" style="width:${r.drawPct}%"></i><i class="ex-b" style="width:${r.blackPct}%"></i>
        <b class="ex-wn">${r.whitePct}%</b><b class="ex-bn">${r.blackPct}%</b>
      </span>`;
    btn.querySelector('.ex-san')!.textContent = r.san;
    btn.querySelector('.ex-pop')!.textContent = `${r.popPct}%`;
    btn.addEventListener('click', () => playRow(fen, r.uci));
    return btn;
  }

  function playRow(fen: string, uci: string): void {
    if (play?.active || fen !== nav.fen) return;
    const applied = applyUci(fen, uci);
    if (!applied) return;
    haptic();
    nav.push({ fen: applied.fen, last: { from: applied.from, to: applied.to } }); // новая ветка → redo чистится
    drawBoard();
    void drawMoves();
  }

  function browseMove(fenAfter: string): void {
    // ход с доски пальцем — тоже углубляем ветку (last-marker берём диффом позиций)
    nav.push({ fen: fenAfter, last: null }); // новая ветка → redo чистится
    drawBoard();
    void drawMoves();
  }

  function selectOpening(op: TopOpening | null): void {
    current = op;
    if (op && op.line.length) {
      const last = op.line[op.line.length - 1]!;
      nav.reset(last.fen, { from: last.uci.slice(0, 2), to: last.uci.slice(2, 4) });
      orient = op.orientation;
      nameEl.textContent = op.name;
    } else {
      nav.reset(START_FEN, null);
      orient = 'white';
      nameEl.textContent = t('topen.start_name');
    }
    renderChips();
    drawBoard();
    void drawMoves();
  }

  function renderChips(): void {
    const startChip = document.createElement('button');
    startChip.type = 'button';
    startChip.className = `topen-chip${current === null ? ' active' : ''}`;
    startChip.innerHTML = `<b>${esc(t('topen.start_name'))}</b>`;
    startChip.addEventListener('click', () => {
      if (current !== null) selectOpening(null);
    });
    chipsEl.replaceChildren(
      startChip,
      ...opens.map((op) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = `topen-chip${op === current ? ' active' : ''}`;
        b.innerHTML = `<b>${esc(op.name)}</b><span>${t('topen.games_n', { n: op.count })}</span>`;
        b.addEventListener('click', () => {
          if (op !== current) selectOpening(op);
        });
        return b;
      }),
    );
    chipsEl.classList.toggle('hidden', !opens.length); // без своих дебютов чип «С начала» одинок — скрываем, старт и так дефолт
  }

  // Показ/скрытие браузера веток на время игры с ботом (тулбар остаётся — 🤖 тумблер).
  function setPlayingUi(playing: boolean): void {
    nameEl.parentElement?.classList.toggle('hidden', playing);
    movesEl.classList.toggle('hidden', playing);
  }

  // --- единый тулбар (⏮ ◀ ▶ ⏭ · 💡 🤖 ⇅) --------------------------------------
  function step(fn: () => void): void {
    fn();
    drawBoard();
    void drawMoves();
  }
  const host: BoardHost = {
    first: () => step(() => nav.toStart()),
    prev: () => step(() => nav.back()),
    next: () => step(() => nav.forward()),
    last: () => step(() => nav.toEnd()),
    navState: () => ({ atStart: nav.atStart, atEnd: nav.atEnd }),
    flip: () => {
      orient = orient === 'white' ? 'black' : 'white';
      board.setOrientation(orient);
    },
    applyBest: (show) => {
      showBest = show;
      updateArrow();
    },
    startPlay: () => {
      trackTraining('play_engine');
      setPlayingUi(true);
      play!.start(nav.fen, orient === 'white' ? 'w' : 'b');
    },
    play: play!,
  };
  const toolbar = new BoardToolbar(host);
  root.querySelector<HTMLElement>('[data-nav]')!.replaceWith(toolbar.el);

  const opens = topOpenings(state.games, state.nick, { limit: 12 }); // все семейства, лента скроллится
  selectOpening(opens[0] ?? null);
}
